<template>
   <div class="migration-layout">
      <!-- Header Section with Stats -->
      <div class="header-section p-mb-4">
         <div class="flex justify-content-between align-items-center mb-4">
            <div>
               <h1 class="text-3xl font-bold text-900 m-0">Datenbank Migrationen</h1>
               <p class="text-600 mt-2">Verwalten und prüfen Sie Datenbank-Schema-Änderungen</p>
            </div>
            <div>
               <Button icon="pi pi-refresh" label="Aktualisieren" @click="fetchMigrations" :loading="loading"
                  class="p-button-outlined mr-2" />
            </div>
         </div>

         <div class="grid">
            <div class="col-12 md:col-4">
               <Card class="surface-card shadow-1 p-3 border-round border-left-3 border-blue-500">
                  <template #content>
                     <div class="flex justify-content-between align-items-center">
                        <div>
                           <span class="block text-500 font-medium mb-2">Ausstehend</span>
                           <div class="text-900 font-medium text-xl">{{ pendingMigrations.length }}</div>
                        </div>
                        <div class="flex align-items-center justify-content-center bg-blue-100 border-round"
                           style="width: 2.5rem; height: 2.5rem">
                           <i class="pi pi-clock text-blue-500 text-xl"></i>
                        </div>
                     </div>
                  </template>
               </Card>
            </div>
            <div class="col-12 md:col-4">
               <Card class="surface-card shadow-1 p-3 border-round border-left-3 border-green-500">
                  <template #content>
                     <div class="flex justify-content-between align-items-center">
                        <div>
                           <span class="block text-500 font-medium mb-2">Erfolgreich</span>
                           <div class="text-900 font-medium text-xl">{{ successfulCount }}</div>
                        </div>
                        <div class="flex align-items-center justify-content-center bg-green-100 border-round"
                           style="width: 2.5rem; height: 2.5rem">
                           <i class="pi pi-check text-green-500 text-xl"></i>
                        </div>
                     </div>
                  </template>
               </Card>
            </div>
            <div class="col-12 md:col-4">
               <Card class="surface-card shadow-1 p-3 border-round border-left-3 border-red-500">
                  <template #content>
                     <div class="flex justify-content-between align-items-center">
                        <div>
                           <span class="block text-500 font-medium mb-2">Fehlgeschlagen</span>
                           <div class="text-900 font-medium text-xl">{{ failedCount }}</div>
                        </div>
                        <div class="flex align-items-center justify-content-center bg-red-100 border-round"
                           style="width: 2.5rem; height: 2.5rem">
                           <i class="pi pi-exclamation-triangle text-red-500 text-xl"></i>
                        </div>
                     </div>
                  </template>
               </Card>
            </div>
         </div>
      </div>

      <!-- Main Content TabView -->
      <TabView class="migration-tabs shadow-1 border-round surface-card">
         <TabPanel header="Ausstehend">
            <DataTable :value="pendingMigrations" :loading="loading" showGridlines stripedRows responsiveLayout="scroll"
               class="p-datatable-sm">
               <template #empty>
                  <div class="text-center p-4">
                     <i class="pi pi-check-circle text-green-500 text-4xl mb-3"></i>
                     <p class="m-0">System ist auf dem neuesten Stand. Keine ausstehenden Migrationen.</p>
                  </div>
               </template>
               <Column field="name" header="Migrations-Datei" sortable></Column>
               <Column header="Aktionen" style="width: 15rem; text-align: center;">
                  <template #body="slotProps">
                     <Button label="Ausführen" icon="pi pi-play" class="p-button-success p-button-sm"
                        @click="openRunDialog(slotProps.data.name)" />
                  </template>
               </Column>
            </DataTable>
         </TabPanel>
         <TabPanel header="Verlauf">
            <DataTable :value="pastMigrations" :loading="loading" showGridlines stripedRows :paginator="true" :rows="10"
               sortField="startedAt" :sortOrder="-1" responsiveLayout="scroll" class="p-datatable-sm">
               <template #empty>Keine vergangenen Migrationen gefunden.</template>
               <Column field="migrationName" header="Name" sortable></Column>
               <Column field="status" header="Status" sortable style="width: 10rem">
                  <template #body="slotProps">
                     <Tag :value="getStatusLabel(slotProps.data.status)"
                        :severity="getStatusColor(slotProps.data.status)" rounded></Tag>
                  </template>
               </Column>
               <Column field="startedAt" header="Gestartet" sortable style="width: 12rem">
                  <template #body="slotProps">
                     {{ formatDate(slotProps.data.startedAt) }}
                  </template>
               </Column>
               <Column field="finishedAt" header="Beendet" sortable style="width: 12rem">
                  <template #body="slotProps">
                     {{ formatDate(slotProps.data.finishedAt) }}
                  </template>
               </Column>
               <Column header="Details" style="width: 8rem; text-align: center;">
                  <template #body="slotProps">
                     <Button icon="pi pi-eye" class="p-button-text p-button-secondary p-button-sm"
                        @click="showLog(slotProps.data)" v-tooltip.top="'Log ansehen'" />
                  </template>
               </Column>
            </DataTable>
         </TabPanel>
      </TabView>

      <!-- Run Dialog -->
      <Dialog v-model:visible="showRunDialog" header="Migration ausführen" :modal="true" :style="{ width: '400px' }"
         :closable="!running">
         <div class="flex flex-column gap-3">
            <div class="field">
               <label for="migration" class="font-bold block mb-2">Migration</label>
               <InputText id="migration" v-model="selectedMigration" disabled class="w-full" />
            </div>
            <div class="field">
               <label for="dbname" class="font-bold block mb-2">Datenbank Name (Optional)</label>
               <InputText id="dbname" v-model="dbName" placeholder="Leer lassen für Standard" class="w-full"
                  :disabled="running" />
               <small class="text-500">Falls leer, wird die Standard-DB verwendet.</small>
            </div>
         </div>
         <template #footer>
            <Button label="Abbrechen" icon="pi pi-times" @click="showRunDialog = false" class="p-button-text"
               :disabled="running" />
            <Button label="Starten" icon="pi pi-play" @click="runMigration" :loading="running" class="p-button-success"
               :disabled="running" />
         </template>
      </Dialog>

      <!-- Live Execution Dialog -->
      <Dialog v-model:visible="showExecutionDialog" header="Migration wird ausgeführt..." :modal="true"
         :closable="!isExecuting" :draggable="false" :style="{ width: '90vw', maxWidth: '1200px' }"
         class="migration-execution-dialog">
         <BlockUI :blocked="isExecuting" fullScreen>
            <div class="execution-container">
               <!-- Status Header -->
               <div class="status-header p-3 mb-3 border-round bg-primary-50 border-1 border-primary-200">
                  <div class="flex justify-content-between align-items-center">
                     <div>
                        <h3 class="m-0 mb-2">{{ currentMigrationName }}</h3>
                        <div class="flex align-items-center gap-2">
                           <ProgressSpinner v-if="isExecuting" style="width: 20px; height: 20px;" strokeWidth="4" />
                           <Tag v-if="!isExecuting && executionStatus === 'success'" severity="success"
                              value="Erfolgreich" />
                           <Tag v-if="!isExecuting && executionStatus === 'error'" severity="danger"
                              value="Fehlgeschlagen" />
                           <span class="text-600">{{ executionStatusText }}</span>
                        </div>
                     </div>
                     <div class="text-right">
                        <div class="text-sm text-600">Gestartet: {{ formatTime(executionStartTime) }}</div>
                        <div v-if="!isExecuting && executionEndTime" class="text-sm text-600">Beendet: {{
                           formatTime(executionEndTime) }}</div>
                        <div v-if="!isExecuting && executionDuration" class="text-sm font-bold">Dauer: {{
                           executionDuration
                        }}s</div>
                     </div>
                  </div>
               </div>

               <!-- Live Log Output -->
               <div class="log-container border-round border-1 surface-border bg-gray-900 p-3 overflow-auto"
                  style="height: 500px; font-family: 'Courier New', monospace;">
                  <div v-if="executionLogs.length === 0"
                     class="text-gray-400 flex align-items-center justify-content-center" style="height: 100%">
                     <div class="text-center">
                        <ProgressSpinner style="width: 40px; height: 40px;" strokeWidth="4" />
                        <p class="mt-3">Warte auf Log-Ausgabe...</p>
                     </div>
                  </div>
                  <div v-for="(log, index) in executionLogs" :key="index" class="log-line mb-1">
                     <span class="text-gray-500 mr-2">{{ log.timestamp }}</span>
                     <span :class="getLogClass(log.type)">{{ log.message }}</span>
                  </div>
                  <div ref="logEnd"></div>
               </div>
            </div>
         </BlockUI>
         <template #footer>
            <Button label="Schließen" @click="closeExecutionDialog" :disabled="isExecuting" class="p-button-text" />
            <Button v-if="!isExecuting && executionStatus === 'success'" label="Aktualisieren" icon="pi pi-refresh"
               @click="closeAndRefresh" class="p-button-success" />
         </template>
      </Dialog>

      <!-- Log Dialog -->
      <Dialog v-model:visible="showLogDialog" header="Protokoll Details" :modal="true" :style="{ width: '70vw' }"
         :maximizable="true">
         <div v-if="selectedLog" class="flex flex-column h-full">
            <div class="grid mb-3">
               <div class="col-6"><strong>Migration:</strong> {{ selectedLog.migrationName }}</div>
               <div class="col-6 text-right">
                  <Tag :value="getStatusLabel(selectedLog.status)" :severity="getStatusColor(selectedLog.status)" />
               </div>
               <div class="col-6"><strong>Start:</strong> {{ formatDate(selectedLog.startedAt) }}</div>
               <div class="col-6"><strong>Ende:</strong> {{ formatDate(selectedLog.finishedAt) }}</div>
               <div class="col-12"><strong>DB:</strong> {{ selectedLog.db || 'Default' }}</div>
            </div>

            <div class="log-viewer border-1 surface-border border-round bg-black-alpha-90 text-white p-3 overflow-auto"
               style="min-height: 300px; max-height: 60vh; font-family: monospace;">
               <div v-if="selectedLog.log" class="mb-3">
                  <div class="text-green-400 font-bold mb-1">STDOUT ></div>
                  <pre class="m-0 white-space-pre-wrap">{{ selectedLog.log }}</pre>
               </div>
               <div v-if="selectedLog.error" class="mt-3">
                  <div class="text-red-400 font-bold mb-1">STDERR ></div>
                  <pre class="m-0 white-space-pre-wrap">{{ selectedLog.error }}</pre>
               </div>
            </div>
         </div>
      </Dialog>
   </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import axiosInstance from '@/plugins/axios';
import { useToast } from 'primevue/usetoast';

// Components
import Button from 'primevue/button';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import BlockUI from 'primevue/blockui';

const toast = useToast();

const loading = ref(false);
const running = ref(false);

const migrations = ref<string[]>([]);
const logs = ref<any[]>([]);

const showRunDialog = ref(false);
const showLogDialog = ref(false);
const selectedMigration = ref('');
const dbName = ref('');
const selectedLog = ref<any>(null);

// Live execution state
const showExecutionDialog = ref(false);
const isExecuting = ref(false);
const executionLogs = ref<Array<{ timestamp: string, type: string, message: string }>>([]);
const currentMigrationName = ref('');
const executionStatus = ref<'pending' | 'success' | 'error'>('pending');
const executionStatusText = ref('Migration wird vorbereitet...');
const executionStartTime = ref<Date | null>(null);
const executionEndTime = ref<Date | null>(null);
const executionDuration = ref<number | null>(null);
const logEnd = ref<HTMLElement | null>(null);
const pollInterval = ref<number | null>(null);
const logId = ref<string | null>(null);

const fetchMigrations = async () => {
   loading.value = true;
   try {
      const res = await axiosInstance.get('/db/migrations');
      migrations.value = res.data.migrations || [];
      logs.value = res.data.logs || [];
   } catch (error) {
      console.error(error);
      toast.add({ severity: 'error', summary: 'Fehler', detail: 'Konnte Migrationen nicht laden.', life: 5000 });
   } finally {
      loading.value = false;
   }
};

const runMigration = async () => {
   running.value = true;
   showRunDialog.value = false;

   // Open execution dialog
   currentMigrationName.value = selectedMigration.value;
   executionLogs.value = [];
   executionStatus.value = 'pending';
   executionStatusText.value = 'Migration wird gestartet...';
   executionStartTime.value = new Date();
   executionEndTime.value = null;
   executionDuration.value = null;
   isExecuting.value = true;
   showExecutionDialog.value = true;

   try {
      const response = await axiosInstance.post('/db/migrations/run', {
         migrationName: selectedMigration.value,
         dbName: dbName.value || undefined
      });

      // Get log ID from response if available
      logId.value = response.data.logId || null;

      addLog('info', `Migration "${selectedMigration.value}" wurde gestartet...`);
      executionStatusText.value = 'Migration läuft...';

      // Start polling for updates
      startPolling();
   } catch (error: any) {
      console.error(error);
      addLog('error', `Fehler beim Starten der Migration: ${error.message}`);
      executionStatus.value = 'error';
      executionStatusText.value = 'Fehler beim Starten';
      isExecuting.value = false;
      running.value = false;
   }
};

const startPolling = () => {
   // Poll every 500ms for updates
   pollInterval.value = window.setInterval(async () => {
      try {
         const res = await axiosInstance.get('/db/migrations/logs');
         const allLogs = res.data.logs || [];

         // Find our migration log - use logId if available, otherwise find most recent matching migration
         let migrationLog;
         if (logId.value) {
            migrationLog = allLogs.find((l: any) => l._id === logId.value);
         } else {
            // Find most recent log for this migration name
            const matchingLogs = allLogs.filter((l: any) => l.migrationName === currentMigrationName.value);
            if (matchingLogs.length > 0) {
               // Sort by startedAt descending and take first
               migrationLog = matchingLogs.sort((a: any, b: any) =>
                  new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
               )[0];
            }
         }

         if (migrationLog) {
            // Clear existing logs before updating
            const newLogs: Array<{ timestamp: string, type: string, message: string }> = [];

            // Update logs from stdout
            if (migrationLog.log) {
               const logLines = migrationLog.log.split('\n').filter((line: string) => line.trim());
               logLines.forEach((line: string) => {
                  newLogs.push({
                     timestamp: new Date().toLocaleTimeString(),
                     type: 'info',
                     message: line
                  });
               });
            }

            // Add error logs if present
            if (migrationLog.error) {
               const errorLines = migrationLog.error.split('\n').filter((line: string) => line.trim());
               errorLines.forEach((line: string) => {
                  newLogs.push({
                     timestamp: new Date().toLocaleTimeString(),
                     type: 'error',
                     message: line
                  });
               });
            }

            executionLogs.value = newLogs;

            // Check if finished
            if (migrationLog.status === 'success' || migrationLog.status === 'error') {
               executionStatus.value = migrationLog.status;
               executionEndTime.value = new Date(migrationLog.finishedAt);
               executionDuration.value = Math.round((executionEndTime.value.getTime() - new Date(migrationLog.startedAt).getTime()) / 1000);

               if (migrationLog.status === 'success') {
                  executionStatusText.value = 'Migration erfolgreich abgeschlossen!';
                  addLog('success', '✓ Migration erfolgreich abgeschlossen');
                  toast.add({ severity: 'success', summary: 'Erfolgreich', detail: 'Migration wurde erfolgreich ausgeführt', life: 3000 });
               } else {
                  executionStatusText.value = 'Migration fehlgeschlagen';
                  addLog('error', '✗ Migration fehlgeschlagen');
                  toast.add({ severity: 'error', summary: 'Fehler', detail: 'Migration ist fehlgeschlagen', life: 5000 });
               }

               stopPolling();
               isExecuting.value = false;
               running.value = false;
            }
         }
      } catch (error) {
         console.error('Polling error:', error);
      }
   }, 500);
};

const stopPolling = () => {
   if (pollInterval.value) {
      clearInterval(pollInterval.value);
      pollInterval.value = null;
   }
};

const addLog = (type: string, message: string) => {
   executionLogs.value.push({
      timestamp: new Date().toLocaleTimeString(),
      type,
      message
   });

   // Auto-scroll to bottom
   nextTick(() => {
      if (logEnd.value) {
         logEnd.value.scrollIntoView({ behavior: 'smooth' });
      }
   });
};

const getLogClass = (type: string) => {
   switch (type) {
      case 'error': return 'text-red-400';
      case 'success': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-gray-300';
   }
};

const closeExecutionDialog = () => {
   stopPolling();
   showExecutionDialog.value = false;
   executionLogs.value = [];
};

const closeAndRefresh = () => {
   closeExecutionDialog();
   fetchMigrations();
};

const openRunDialog = (name: string) => {
   selectedMigration.value = name;
   dbName.value = '';
   showRunDialog.value = true;
};

const showLog = (log: any) => {
   selectedLog.value = log;
   showLogDialog.value = true;
};

const formatTime = (date: Date | null) => {
   if (!date) return '-';
   return date.toLocaleTimeString();
};

// --- Computed ---

const pendingMigrations = computed(() => {
   // Successful migrations logic:
   // A migration is pending if it exists in the file list AND hasn't been logged as 'success' recently (or ever)
   // For simplicity, if we have a success log for this name, we count it as done.
   const doneSet = new Set(logs.value.filter(l => l.status === 'success').map(l => l.migrationName));
   return migrations.value.filter(m => !doneSet.has(m)).map(name => ({ name }));
});

const pastMigrations = computed(() => {
   return logs.value;
});

const successfulCount = computed(() => logs.value.filter(l => l.status === 'success').length);
const failedCount = computed(() => logs.value.filter(l => l.status === 'error').length);

// --- Helpers ---

const getStatusLabel = (status: string) => {
   switch (status) {
      case 'success': return 'Erfolgreich';
      case 'error': return 'Fehler';
      case 'pending': return 'Laufend';
      default: return status;
   }
};

const getStatusColor = (status: string): "success" | "danger" | "info" | "secondary" | "warning" | "contrast" | undefined => {
   switch (status) {
      case 'success': return 'success';
      case 'error': return 'danger';
      case 'pending': return 'warning';
      default: return 'info';
   }
};

const formatDate = (dateStr: string) => {
   if (!dateStr) return '-';
   return new Date(dateStr).toLocaleString();
};

onMounted(() => {
   fetchMigrations();
});
</script>

<style scoped>
/* Utility classes used: p-4, p-mb-4, text-3xl, font-bold, etc. from PrimeFlex */
/* Custom adjustments if needed */
.migration-layout {
   max-width: 1400px;
   margin: 0 auto;
}

.execution-container {
   min-height: 600px;
}

.log-container {
   background: #1e1e1e;
   color: #d4d4d4;
}

.log-line {
   font-size: 13px;
   line-height: 1.5;
   white-space: pre-wrap;
   word-break: break-all;
}

.migration-execution-dialog :deep(.p-dialog-content) {
   padding: 0;
}

.migration-execution-dialog :deep(.p-blockui) {
   background-color: rgba(0, 0, 0, 0.4);
}
</style>
